import * as Sentry from '@sentry/react';
import { VStack } from '@televet/kibble-ui';
import { Modal, ModalBody, ModalFooterButtons, ModalHeader } from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import useClinicUser from 'shared/hooks/useClinicUser';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';
import usePersistedState from 'shared/hooks/usePersistedState';
import usePrevious from 'shared/hooks/usePrevious';
import { firebase } from 'shared/lib';
import { useAuth } from 'shared/providers/AuthProvider';
import { UserType, useUpdateCreatePasswordModalUserMutation } from 'shared/types/graphql';

type CreatePasswordModalProps = {
  onClose: () => void;
};

const CreatePasswordModal = ({ onClose }: CreatePasswordModalProps): ReactElement => {
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [passwordValidationErrorMessage, setPasswordValidationErrorMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();
  const { clinicUser } = useClinicUser();
  const [invitationAuthToken, setInvitationAuthToken] = usePersistedState<string>(
    PersistedStateKeys.InvitationAuthToken,
    '',
  );
  const { loginWithToken } = useAuth();
  const [updateUser] = useUpdateCreatePasswordModalUserMutation();

  const onSetPassword = useCallback(async (): Promise<void> => {
    const firebaseUser = firebase.auth().currentUser;
    if (!(firebaseUser && clinicUser)) return;

    if (newPassword.length < 8) {
      setPasswordValidationErrorMessage('Please provide a password with at least 8 characters');
    } else if (newPasswordConfirmation !== newPassword) {
      setPasswordValidationErrorMessage("Hmmm, those passwords don't look alike. Please confirm they match.");
    } else {
      setIsUpdating(true);
      try {
        await loginWithToken(invitationAuthToken);
        setInvitationAuthToken('');
        await firebaseUser.updatePassword(newPassword);
        await updateUser({
          variables: {
            where: { firebaseId: firebaseUser.uid },
            data: { isNewPasswordRequired: false, type: UserType.Vet },
          },
        });
        setIsUpdating(false);
        toast({
          status: 'success',
          title: 'Nice job! Your password was created successfully.',
        });
        onClose();
      } catch (error) {
        Sentry.captureException(error);
        // TODO: Handle expired auth token error
        console.error(error);
        setIsUpdating(false);
        setPasswordValidationErrorMessage(error.message);
      }
    }
  }, [
    clinicUser,
    newPassword,
    newPasswordConfirmation,
    invitationAuthToken,
    loginWithToken,
    setInvitationAuthToken,
    updateUser,
    toast,
    onClose,
  ]);

  const previousValidationMessage = usePrevious(passwordValidationErrorMessage);
  useEffect(() => {
    if (!!previousValidationMessage) {
      setPasswordValidationErrorMessage('');
    }
  }, [previousValidationMessage, newPassword, newPasswordConfirmation, setPasswordValidationErrorMessage]);

  const { hasModule } = useModularity();
  const productName = hasModule(ModuleName.AppointmentsRecap) ? 'Otto Recap' : 'Otto Flow';

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        onSetPassword();
      }
    },
    [onSetPassword],
  );

  return (
    <Modal size="sm" isOpen={true} onClose={onClose} allowEscape={false}>
      <ModalHeader>👋 Welcome to {productName}!</ModalHeader>
      <ModalBody>
        <VStack gap={2} spacing={0} mb={2} justifyContent="flex-start" alignItems="flex-start">
          <Text as="p" my={4} size="md">
            Before we begin, let&apos;s get you started on the right paw with a brand new password.
          </Text>
          <TextInput
            autoFocus={true}
            type="password"
            autoComplete="off"
            label={
              <Text>
                Password{' '}
                <Text ml={2} color="text.subtle" size="xs">
                  Minimum of 8 characters
                </Text>
              </Text>
            }
            onKeyDown={handleKeyDown}
            value={newPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setNewPassword(e.target.value);
            }}
          />
          <TextInput
            type="password"
            label="Confirm password"
            autoComplete="off"
            value={newPasswordConfirmation}
            onKeyDown={handleKeyDown}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setNewPasswordConfirmation(e.target.value);
            }}
          />
          <Text textColor="text.danger" size="xs">
            {passwordValidationErrorMessage}
          </Text>
        </VStack>
      </ModalBody>
      <ModalFooterButtons allowCancel={false} isLoading={isUpdating} submitText="Let's go" onSubmit={onSetPassword} />
    </Modal>
  );
};

export default CreatePasswordModal;
